// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { Button, Flex, TrashIcon, Typography } from '@iheartjane/reefer';
import { spacing } from '@iheartjane/reefer-emotion';
import { Form, useFormContext } from '@iheartjane/reefer-hook-form';
import type { DeliveryZipcode } from '@iheartjane/types';
import keyBy from 'lodash-es/keyBy';
import omit from 'lodash-es/omit';
import { useCallback, useContext, useEffect, useState } from 'react';

import { FulfillmentSettingsContext } from '@jane/business-admin/providers';
import { EventNames, ModalNames, track } from '@jane/business-admin/util';

import { DollarInput } from '../../../../../../../DollarInput';

// Using zip in a field name will make 1password extension show up
const FIELD_ARRAY_NAME = 'delivery_zcodes';
const LIMIT_ZIP_CODE_SETTINGS_TO_DISPLAY = 15;

const DEFAULT_NEW_ZIPCODE = {
  zipcode: undefined,
  fee: 0,
  min_amount: 0,
  lead_time_minutes: 0,
  last_call_minutes: 0,
};

// This is needed to make sure the top-level labels line up with the inputs
// Matches the "TrashIcon" button sizing and spacing
const ButtonGap = styled.div({
  width: '100%',
  maxWidth: 48,
  ...spacing({ ml: 12 }),
});

const HeaderWrapper = styled.div({
  width: '100%',
});

type FormStateDeliveryZipcodes = Record<string, DeliveryZipcode>;
type UsedZipcodeMap = Map<number, string>;

const getFieldNamePrefix = (id: string | number) =>
  `${FIELD_ARRAY_NAME}.${id}` as const;

const ZipcodeItem = ({
  id,
  index,
  setting,
  removeItem,
  usedZipcodes,
  updateUsedZipcodes,
}: {
  id: string | number;
  index: number;
  notUnique: boolean;
  removeItem: (id: string | number) => void;
  setting: DeliveryZipcode;
  updateUsedZipcodes: (value: string, id: string | number) => void;
  usedZipcodes: UsedZipcodeMap;
}) => {
  const nameConcat = getFieldNamePrefix(id);
  const { setValue, watch } = useFormContext();
  const formFee = watch(`${nameConcat}.fee`);
  const formMinAmount = watch(`${nameConcat}.min_amount`);

  useEffect(() => {
    if (formFee !== undefined) {
      const converted =
        typeof formFee === 'string' ? parseFloat(formFee) : formFee;
      setValue(`${nameConcat}.fee`, converted);
    }
  }, [formFee]);

  useEffect(() => {
    if (formMinAmount !== undefined) {
      const converted =
        typeof formMinAmount === 'string'
          ? parseFloat(formMinAmount)
          : formMinAmount;
      setValue(`${nameConcat}.min_amount`, converted);
    }
  }, [formMinAmount]);

  const validateZipcodeInput = useCallback(
    (zipcodeValue: string) => {
      if (zipcodeValue) {
        const validLength = /^\d{5}$/.test(zipcodeValue);
        let notUnique = false;

        for (const [key, zipcode] of usedZipcodes.entries()) {
          if (zipcode === zipcodeValue && id !== key) {
            notUnique = true;
          }
        }

        if (!validLength) {
          return 'Only 5-digit US zipcodes are supported.';
        } else if (notUnique) {
          return 'Please enter a unique zipcode.';
        }
      }
      return true;
    },
    [index, usedZipcodes]
  );

  return (
    <Flex mb={24}>
      <Flex gap={32} width="100%">
        <Form.TextField
          defaultValue={setting.zipcode}
          name={`${nameConcat}.zipcode`}
          label="Zipcode"
          labelHidden
          width="100%"
          onChange={(value: string) => updateUsedZipcodes(value, id)}
          validate={validateZipcodeInput}
          shouldUnregister={false}
          required
        />
        <DollarInput
          defaultValue={parseFloat(setting.fee)}
          name={`${nameConcat}.fee`}
          label="Delivery fee"
          labelHidden
          shouldUnregister={false}
          required
        />
        <DollarInput
          defaultValue={parseFloat(setting.min_amount)}
          name={`${nameConcat}.min_amount`}
          label="Delivery minimum"
          labelHidden
          shouldUnregister={false}
          required
        />
        <Form.NumberField
          defaultValue={setting.lead_time_minutes}
          name={`${nameConcat}.lead_time_minutes`}
          label="Lead time"
          labelHidden
          endUnit="minutes"
          width="100%"
          shouldUnregister={false}
          required
        />
        <Form.NumberField
          defaultValue={setting.last_call_minutes}
          name={`${nameConcat}.last_call_minutes`}
          label="Last call"
          labelHidden
          endUnit="minutes"
          width="100%"
          shouldUnregister={false}
          required
        />
        <Button.Icon
          onClick={() => removeItem(id)}
          label="remove zipcode setting"
          icon={<TrashIcon />}
          ml={12}
        />
      </Flex>
    </Flex>
  );
};

export const ZipcodeSettings = () => {
  const {
    storePayload: { delivery_zipcodes },
  } = useContext(FulfillmentSettingsContext);

  const { watch, setValue } = useFormContext();

  const zipcodeSettings: FormStateDeliveryZipcodes =
    watch(FIELD_ARRAY_NAME) || {};

  // Track used zipcodes to validate uniqueness as they are entered in the form
  const [usedZipcodes, setUsedZipcodes] = useState<UsedZipcodeMap>(new Map());
  useEffect(() => {
    const usedZipcodesMap = new Map();
    Object.values(zipcodeSettings).forEach(({ zipcode, id }) => {
      usedZipcodesMap.set(id, zipcode);
    });

    setUsedZipcodes(usedZipcodesMap);
  }, [JSON.stringify(zipcodeSettings)]);

  const [showAll, setShowAll] = useState<boolean>(false);
  const zipcodeCount = Object.keys(zipcodeSettings).length;

  // we will only show up to 15 by default. User will need to expand the list if they want to see more.
  const maxZipCodeSettingsExceeded =
    zipcodeCount > LIMIT_ZIP_CODE_SETTINGS_TO_DISPLAY;

  useEffect(() => {
    if (!Object.entries(zipcodeSettings).length) {
      setValue(FIELD_ARRAY_NAME, keyBy(delivery_zipcodes, 'id'));
    }
  }, []);

  const addNewZipcode = () => {
    setValue(
      FIELD_ARRAY_NAME,
      {
        ...zipcodeSettings,
        [`new-${zipcodeCount}`]: {
          id: `new-${zipcodeCount}`,
          ...DEFAULT_NEW_ZIPCODE,
        },
      },
      { shouldDirty: true }
    );
  };

  const removeZipcode = (id: string | number) => {
    track({
      event: EventNames.DeleteStoreEntity,
      modal_name: ModalNames.DeliverySettings,
      entity_name: 'zipcodes',
    });
    setValue(FIELD_ARRAY_NAME, omit(zipcodeSettings, id), {
      shouldDirty: true,
    });
    removeUsedZipcode(id);
  };

  const removeUsedZipcode = (id: string | number) => {
    usedZipcodes.delete(id as any);
    setUsedZipcodes(usedZipcodes);
  };

  const updateUsedZipcodes = (zipcodeValue: string, id: string | number) => {
    usedZipcodes.set(id as any, zipcodeValue);
  };

  return (
    <Flex flexDirection="column" mt={40}>
      <Flex mb={20} gap={32} width="100%">
        <HeaderWrapper>
          <Typography>Zipcode</Typography>
        </HeaderWrapper>
        <HeaderWrapper>
          <Typography>Delivery fee</Typography>
        </HeaderWrapper>
        <HeaderWrapper>
          <Typography>Delivery minimum</Typography>
        </HeaderWrapper>
        <HeaderWrapper>
          <Typography>Lead time</Typography>
        </HeaderWrapper>
        <HeaderWrapper>
          <Typography>Last call</Typography>
        </HeaderWrapper>
        <ButtonGap />
      </Flex>

      {Object.values(zipcodeSettings).map((setting, index) => {
        const isString =
          typeof setting.id === 'string' &&
          (setting.id as string).indexOf('new-') >= 0;

        if (
          !isString &&
          !showAll &&
          index >= LIMIT_ZIP_CODE_SETTINGS_TO_DISPLAY
        ) {
          return null;
        }

        return (
          <ZipcodeItem
            key={setting.id}
            setting={setting as unknown as DeliveryZipcode}
            index={index}
            notUnique={false}
            id={setting.id || `new-${zipcodeCount}`}
            removeItem={removeZipcode}
            usedZipcodes={usedZipcodes}
            updateUsedZipcodes={updateUsedZipcodes}
          />
        );
      })}

      <Flex justifyContent="center" mt={8}>
        <Flex width={200}>
          <Button
            onClick={addNewZipcode}
            full
            label="Add new"
            variant="secondary"
          />
          {maxZipCodeSettingsExceeded && (
            <Button
              ml={12}
              onClick={() => setShowAll(!showAll)}
              full
              label={showAll ? 'See less' : 'See all'}
              variant="secondary"
            />
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};
